import React, { useState, useEffect } from 'react';
import { Newspaper, ExternalLink, Clock, X, FileText } from 'lucide-react';
import { USER_ROLES } from '../utils/roles';
import { db } from '../firebase/config';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import NewsSummaryModal from './NewsSummaryModal';
import ArticleContentModal from './ArticleContentModal';
import CompanyLogo from './CompanyLogo';

export default function NewsFeed({ ticker = 'PLUG', userRole, maxItems, compact = false }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryArticle, setSummaryArticle] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchNews();
    fetchCompanyInfo();
  }, [ticker]);

  const fetchCompanyInfo = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/companies/${ticker}`);
      const data = await response.json();
      if (data.success) {
        setCompanyInfo(data.company);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const handleDeleteArticle = async (article) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/news/${article.id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setNews(prev => prev.map(n => 
          n.id === article.id ? { ...n, scrapedFullText: null, parsedAt: null } : n
        ));
      } else {
        alert('Failed to clear article content');
      }
    } catch (error) {
      console.error('Error clearing article content:', error);
      alert('Error clearing article content');
    }
  };




  const showSummary = (article) => {
    setSummaryArticle(article);
    setShowSummaryModal(true);
  };

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);

      // Query Firebase Firestore directly
      const newsRef = collection(db, 'news');
      const q = query(
        newsRef,
        where('ticker', '==', ticker),
        orderBy('createdAt', 'desc'),
        limit(50) // Get more articles to ensure we have enough after sorting by publishedAt
      );

      const querySnapshot = await getDocs(q);
      const newsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          publishedAt: typeof data.publishedAt === 'string'
            ? new Date(data.publishedAt)
            : data.publishedAt?.toDate?.() || new Date()
        };
      });

      // Sort by publishedAt (original article publication date) in reverse chronological order
      newsData.sort((a, b) => b.publishedAt - a.publishedAt);

      setNews(maxItems ? newsData.slice(0, maxItems) : newsData); // Take top items after sorting or all if no limit
    } catch (err) {
      // Check if it's an index error
      if (err.message && err.message.includes('index')) {
        setError('Firebase index is being created. Please wait 1-2 minutes and refresh the page.');
      } else if (err.message && err.message.includes('permission')) {
        setError('Permission denied. Please check Firebase security rules.');
      } else {
        setError('Failed to load news');
      }
      console.error('News loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">뉴스를 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchNews}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <>
      <div className={compact ? "" : "space-y-4"}>
        {!compact && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <Newspaper className="w-6 h-6 text-blue-600" />
              {ticker} 최신 뉴스
            </h3>
            {userRole === USER_ROLES.ADMIN && (
              <button
                onClick={fetchNews}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                새로고침
              </button>
            )}
          </div>
        )}

        {news.length === 0 ? (
          <div className="text-center text-gray-500 py-4 text-sm">
            뉴스가 없습니다
          </div>
        ) : (
          <div className={compact ? "space-y-3 max-h-[400px] overflow-y-auto" : "space-y-3"}>
            {news.map((item) => (
              <article
                key={item.id}
                className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow bg-white"
              >
                {/* Line 1: Logo + Title */}
                <div className="flex items-start gap-2 mb-2">
                  {/* Company Logo */}
                  <CompanyLogo ticker={item.ticker} size="md" />

                  {/* Title */}
                  <h4 className="font-semibold text-sm text-gray-900 line-clamp-2 flex-1">
                    {item.titleKr || item.title}
                  </h4>
                </div>

                {/* Line 2: Date (left) & Source (right) */}
                <div className="flex items-center justify-between text-xs mb-2">
                  <div className="flex items-center gap-1 text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>{new Date(item.publishedAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}</span>
                  </div>

                  {item.source && (
                    <span style={{
                      color: item.isOfficialSource ? '#10b981' : '#6b7280',
                      fontWeight: item.isOfficialSource ? '600' : '400'
                    }}>
                      {item.isOfficialSource ? '🏢 공식' : item.source}
                    </span>
                  )}
                </div>

                {/* Line 3: Summary button (left) & Original button (right) */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => showSummary(item)}
                    className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    <FileText className="w-3 h-3" />
                    요약
                  </button>

                  <button
                    onClick={() => {
                      setSelectedArticle(item);
                    }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <ExternalLink className="w-3 h-3" />
                    원문 보기
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Summary Modal */}
      <NewsSummaryModal
        article={summaryArticle}
        onClose={() => {
          setShowSummaryModal(false);
          setSummaryArticle(null);
        }}
        userRole={userRole}
      />

      {/* Article Modal - ArticleContentModal */}
      {selectedArticle && (
        <ArticleContentModal
          article={selectedArticle}
          onClose={() => setSelectedArticle(null)}
          onDelete={handleDeleteArticle}
          userRole={userRole}
        />
      )}
    </>
  );
}
